import {
  ArrowLeft,
  CalendarBlank,
  Eye,
  MapPin,
  Money,
  Ticket,
  User,
  Users,
} from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { RequireAdmin } from '@/app/auth-guard'
import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { formatCurrency, formatDateTime } from '@/lib/i18n/format'
import { currentLocale } from '@/lib/i18n'
import { resolveMediaUrl } from '@/lib/media'
import { kuniriIconWeight } from '@/lib/icons'

import {
  adminApi,
  type AdminActivityDetail,
  type AdminActivityParticipant,
} from '../api'

type ReviewState =
  | { status: 'loading' }
  | { status: 'ready'; data: AdminActivityDetail }
  | { status: 'error'; error: unknown }

function ReviewCard({
  title,
  children,
}: {
  title?: string
  children: ReactNode
}) {
  return (
    <section className="rounded-[22px] border border-line bg-white p-4 sm:p-5">
      {title ? (
        <h2 className="font-head text-lg font-extrabold text-deep">{title}</h2>
      ) : null}
      <div className={title ? 'mt-2' : ''}>{children}</div>
    </section>
  )
}

function ReviewField({
  icon,
  label,
  value,
}: {
  icon: ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-2.5 py-2">
      <span className="mt-0.5 shrink-0 text-teal" aria-hidden="true">
        {icon}
      </span>
      <dt className="w-[110px] shrink-0 text-[13px] text-muted sm:w-[126px]">
        {label}
      </dt>
      <dd className="min-w-0 flex-1 font-bold break-words text-deep">
        {value}
      </dd>
    </div>
  )
}

function Initial({ name }: { name: string }) {
  return (
    <span
      className="grid size-10 shrink-0 place-items-center rounded-full bg-mint font-bold text-teal"
      aria-hidden="true"
    >
      {name.trim().charAt(0).toLocaleUpperCase() || '?'}
    </span>
  )
}

/**
 * Read-only admin activity review (D-055). Exposes every field the moderation
 * console needs, including visibility, participation mode, price and
 * recurrence. It never mutates the activity.
 */
export function AdminActivityReviewScreen() {
  const { t } = useTranslation()
  const locale = currentLocale()
  const { id } = useParams<{ id: string }>()
  const activityId = id?.trim() ? id : null
  const [state, setState] = useState<ReviewState>({ status: 'loading' })
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!activityId) return
    const controller = new AbortController()
    adminApi
      .getActivity(activityId, { signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setState({ status: 'ready', data })
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setState({ status: 'error', error })
      })
    return () => controller.abort()
  }, [activityId, reloadKey])

  function retry() {
    setState({ status: 'loading' })
    setReloadKey((key) => key + 1)
  }

  const notSpecified = t('admin.review.notSpecified')
  const clean = (value: string | null | undefined) =>
    value?.trim() ? value.trim() : notSpecified

  return (
    <RequireAdmin>
      <main
        id="main-content"
        tabIndex={-1}
        className="min-h-dvh bg-paper px-4 pt-[calc(var(--safe-top)+16px)] pb-[calc(2rem+var(--safe-bottom))] md:px-8 md:pt-8"
      >
        <div className="mx-auto w-full max-w-[860px]">
          <Link
            to="/admin-dashboard/activities"
            className="btn-ghost min-h-11 px-2 text-sm"
          >
            <ArrowLeft size={19} weight={kuniriIconWeight} aria-hidden="true" />
            {t('admin.review.back')}
          </Link>

          <h1 className="mt-4 font-head text-[clamp(1.5rem,4vw,2rem)] font-extrabold text-deep">
            {t('admin.review.heading')}
          </h1>

          {!activityId ? (
            <p className="mt-6 text-sm text-muted">{t('admin.review.unavailable')}</p>
          ) : state.status === 'loading' ? (
            <div className="mt-6">
              <LoadingState labelKey="admin.review.loading" />
            </div>
          ) : state.status === 'error' ? (
            <div className="mt-6">
              <ErrorState
                error={state.error}
                titleKey="admin.review.errorTitle"
                bodyKey="admin.review.unavailable"
                onRetry={retry}
                retryLabelKey="common.retry"
              />
            </div>
          ) : (
            <ReviewScreen data={state.data} clean={clean} locale={locale} />
          )}
        </div>
      </main>
    </RequireAdmin>
  )
}

function ReviewScreen({
  data,
  clean,
  locale,
}: {
  data: AdminActivityDetail
  clean: (value: string | null | undefined) => string
  locale: 'it' | 'en'
}) {
  const { t } = useTranslation()
  const activity = data.activity
  const participants = data.participants
  const cover = resolveMediaUrl(activity.coverImageUrl)
  const start = activity.startAt
    ? formatDateTime(activity.startAt, locale)
    : clean(null)
  const end = activity.endAt
    ? formatDateTime(activity.endAt, locale)
    : null
  const recurrenceWeekday = activity.recurrenceWeekday
  const recurrence =
    activity.isRecurring === true
      ? recurrenceWeekday != null && recurrenceWeekday >= 1 && recurrenceWeekday <= 7
        ? t('common.everyWeekday', {
            weekday: t(
              [
                'common.weekdays.monday',
                'common.weekdays.tuesday',
                'common.weekdays.wednesday',
                'common.weekdays.thursday',
                'common.weekdays.friday',
                'common.weekdays.saturday',
                'common.weekdays.sunday',
              ][recurrenceWeekday - 1],
            ),
          })
        : t('admin.review.recurring')
      : null
  const priceType = activity.priceType ?? 'free'
  const price =
    priceType === 'free'
      ? t('admin.review.free')
      : [
          activity.estimatedPrice != null
            ? t('admin.review.estimatedPrice', {
                amount: formatCurrency(activity.estimatedPrice, locale),
              })
            : null,
          activity.priceDescription?.trim() || null,
        ]
          .filter(Boolean)
          .join(' · ') || t('admin.review.externalPrice')

  return (
    <div className="mt-4 space-y-3.5">
      {cover ? (
        <img
          src={cover}
          alt=""
          className="aspect-[2/1] w-full rounded-[22px] object-cover"
          loading="eager"
        />
      ) : null}

      <ReviewCard>
        <ul className="flex flex-wrap gap-2">
          <li className="rounded-pill bg-mint px-2.5 py-1 text-xs font-bold text-teal">
            {t(`admin.status.${activity.status}`)}
          </li>
          <li className="rounded-pill bg-paper px-2.5 py-1 text-xs font-bold text-deep ring-1 ring-line">
            {activity.category}
          </li>
          {recurrence ? (
            <li className="rounded-pill bg-mint px-2.5 py-1 text-xs font-bold text-teal">
              {recurrence}
            </li>
          ) : null}
        </ul>
        <h2 className="mt-2 font-head text-2xl leading-none font-extrabold text-deep">
          {clean(activity.title)}
        </h2>
        <p className="mt-3 text-[15px] leading-relaxed whitespace-pre-wrap text-deep">
          {clean(activity.description)}
        </p>
      </ReviewCard>

      <ReviewCard title={t('admin.review.detailsTitle')}>
        <dl className="divide-y divide-line">
          <ReviewField
            icon={<CalendarBlank size={18} weight={kuniriIconWeight} />}
            label={t('admin.review.fields.date')}
            value={end ? `${start} – ${end}` : start}
          />
          <ReviewField
            icon={<MapPin size={18} weight={kuniriIconWeight} />}
            label={t('admin.review.fields.city')}
            value={clean(activity.city)}
          />
          <ReviewField
            icon={<Eye size={18} weight={kuniriIconWeight} />}
            label={t('admin.review.fields.visibility')}
            value={t(`admin.review.visibility.${activity.visibility ?? 'public'}`)}
          />
          <ReviewField
            icon={<MapPin size={18} weight={kuniriIconWeight} />}
            label={t('admin.review.fields.place')}
            value={clean(activity.location)}
          />
          <ReviewField
            icon={<MapPin size={18} weight={kuniriIconWeight} />}
            label={t('admin.review.fields.meetingPoint')}
            value={clean(activity.meetingPoint)}
          />
          <ReviewField
            icon={<User size={18} weight={kuniriIconWeight} />}
            label={t('admin.review.fields.creator')}
            value={clean(activity.creator)}
          />
          <ReviewField
            icon={<Users size={18} weight={kuniriIconWeight} />}
            label={t('admin.review.fields.participants')}
            value={`${activity.participants} / ${activity.capacity}`}
          />
          <ReviewField
            icon={<Ticket size={18} weight={kuniriIconWeight} />}
            label={t('admin.review.fields.access')}
            value={t(
              `admin.review.mode.${activity.participationMode ?? 'open'}`,
            )}
          />
          <ReviewField
            icon={<Money size={18} weight={kuniriIconWeight} />}
            label={t('admin.review.fields.price')}
            value={price}
          />
        </dl>
      </ReviewCard>

      <ReviewCard
        title={t('admin.review.participantsTitle', {
          count: participants.length,
        })}
      >
        {participants.length === 0 ? (
          <p className="text-sm text-muted">
            {t('admin.review.noParticipants')}
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {participants.map((participant) => (
              <ParticipantRow
                key={participant.id}
                participant={participant}
              />
            ))}
          </ul>
        )}
      </ReviewCard>
    </div>
  )
}

function ParticipantRow({
  participant,
}: {
  participant: AdminActivityParticipant
}) {
  const { t } = useTranslation()
  return (
    <li className="flex items-center gap-3 py-3">
      <Initial name={participant.name} />
      <div className="min-w-0 flex-1">
        <p className="font-bold break-words text-deep">
          {participant.name}
          {participant.role === 'creator'
            ? ` · ${t('admin.review.creatorBadge')}`
            : null}
        </p>
        <p className="mt-0.5 text-xs leading-relaxed break-words text-muted">
          {[
            t(`admin.status.${participant.status}`),
            participant.accountStatus
              ? t('admin.review.accountStatus', {
                  status: t(`admin.status.${participant.accountStatus}`),
                })
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
    </li>
  )
}
